import { Edit2, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import LoadingSpinner from "../../../components/Loader";
import { Patient } from "../../../utils/api-objects/Patient";
import { useAuth } from "../../../utils/context/AuthContext";
import { useUserData } from "../../../utils/context/UserDataContext";
import { getFormattedDateWithInput } from "../../../utils/Functions";
import { PatientNote, usePatientNotes } from "../hooks/usePatientNotes";

interface NotesBoxProps {
    patient: Patient;
}

const NotesBox: React.FC<NotesBoxProps> = ({ patient }) => {
    const { auth } = useAuth();
    const { userData } = useUserData();
    const { notes, isLoading, error, addNote, updateNote, deleteNote } = usePatientNotes(patient.user_id, auth.userId);
    
    const [isExpanded, setIsExpanded] = useState(true);
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [newNoteContent, setNewNoteContent] = useState("");
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingContent, setEditingContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const editingTextareaRef = useRef<HTMLTextAreaElement>(null);

    // Focus at the end of text when editing starts
    useEffect(() => {
        if (editingNoteId && editingTextareaRef.current) {
            const textarea = editingTextareaRef.current;
            textarea.focus();
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
    }, [editingNoteId]);

    const handleToggle = () => {
        setIsExpanded(prev => !prev);
    };

    const handleAddNote = async () => {
        if (newNoteContent.trim() && !isSubmitting) {
            setIsSubmitting(true);
            try {
                await addNote(newNoteContent, auth.userId || "");
                setNewNoteContent("");
                setIsAddingNote(false);
            } catch (error) {
                console.error("Failed to add note:", error);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleStartEdit = (note: PatientNote) => {
        setEditingNoteId(note.id);
        setEditingContent(note.note);
    };

    const handleSaveEdit = async () => {
        if (editingNoteId && editingContent.trim() && !isSubmitting) {
            setIsSubmitting(true);
            try {
                await updateNote(editingNoteId, editingContent);
                setEditingNoteId(null);
                setEditingContent("");
            } catch (error) {
                console.error("Failed to update note:", error);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditingContent("");
    };

    const handleDeleteNote = async (noteId: string) => {
        if (window.confirm("Sei sicuro di voler eliminare questa nota?")) {
            try {
                await deleteNote(noteId);
            } catch (error) {
                console.error("Failed to delete note:", error);
            }
        }
    };

    const formatDate = (dateString: string) => {
        return getFormattedDateWithInput(dateString);
    };

    if (isLoading) {
        return (
            <div className="space-y-[25px] border rounded-xl p-2 border border-solid border-holifya-grey-shadow shadow-platform-new">
                <div className="font-bold text-[16px] leading-[33px]">Note del paziente</div>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-[25px] border rounded-xl p-2 border border-solid border-holifya-grey-shadow shadow-platform-new">
            <div className="font-bold text-[16px] leading-[33px] flex justify-between items-center">
                <span>Le mie note per questo paziente</span>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setIsAddingNote(true)}
                        className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                        disabled={isSubmitting}
                        title="Aggiungi nota"
                    >
                        <Plus size={18} />
                    </button>
                    <button
                        onClick={handleToggle}
                        className="text-blue-500 text-sm hover:text-blue-700"
                    >
                        {isExpanded ? "Mostra meno" : "Mostra di più"}
                    </button>
                </div>
            </div>
            
            {error && (
                <div className="text-red-500 text-sm p-2 bg-red-50 border border-red-200 rounded">
                    Errore nel caricamento delle note: {error.message}
                </div>
            )}
            
            {isExpanded && (
                <div className="space-y-[20px]">
                    {/* Add new note section */}
                    {isAddingNote && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <textarea
                                value={newNoteContent}
                                onChange={(e) => setNewNoteContent(e.target.value)}
                                placeholder="Scrivi una nuova nota..."
                                className="w-full h-24 p-2 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                                disabled={isSubmitting}
                            />
                            <div className="flex justify-end space-x-2 mt-2">
                                <button
                                    onClick={() => {
                                        setIsAddingNote(false);
                                        setNewNoteContent("");
                                    }}
                                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                    disabled={isSubmitting}
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={handleAddNote}
                                    disabled={!newNoteContent.trim() || isSubmitting}
                                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? "Salvando..." : "Salva"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Notes list */}
                    {notes.length === 0 ? (
                        <div className="text-gray-500 text-center py-4">
                            Nessuna nota presente. Clicca sul pulsante + per aggiungere una nota.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {notes.map((note) => (
                                <div key={note.id} className="border border-gray-200 rounded-lg p-4">
                                    {editingNoteId === note.id ? (
                                        <div>
                                            <textarea
                                                value={editingContent}
                                                onChange={(e) => setEditingContent(e.target.value)}
                                                className="w-full h-24 p-2 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                autoFocus
                                                disabled={isSubmitting}
                                                ref={editingTextareaRef}
                                            />
                                            <div className="flex justify-between items-center mt-2">
                                                <div className="text-xs text-gray-500">
                                                    Ultima modifica: {formatDate(note.updated_at)}
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={handleCancelEdit}
                                                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                                        disabled={isSubmitting}
                                                    >
                                                        Annulla
                                                    </button>
                                                    <button
                                                        onClick={handleSaveEdit}
                                                        disabled={!editingContent.trim() || isSubmitting}
                                                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                                    >
                                                        {isSubmitting ? "Salvando..." : "Salva"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="whitespace-pre-wrap text-sm">{note.note}</div>
                                            <div className="flex justify-between items-center mt-2">
                                                <div className="text-xs text-gray-500">
                                                    {note.updated_at === note.created_at 
                                                        ? `Creato: ${formatDate(note.created_at)}`
                                                        : `Creato: ${formatDate(note.created_at)} • Modificato: ${formatDate(note.updated_at)}`
                                                    }
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleStartEdit(note)}
                                                        className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                                        disabled={isSubmitting}
                                                        title="Modifica nota"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteNote(note.id)}
                                                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                        disabled={isSubmitting}
                                                        title="Elimina nota"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotesBox; 